import { useEffect } from "react";
import { motion } from "framer-motion";
import { RefreshCw, TrendingUp, Star, HeartHandshake, Ban, ArrowRight } from "lucide-react";
import { RevShell, ModuleBar, RevHero, SectionHead, RevCTA, BrowserFrame, ComplianceNote } from "./_shared";

const REVIVAL = [
  "Detect inactive, unresponsive & closed-lost leads",
  "Wait an appropriate interval, then re-check circumstances",
  "Generate a genuinely relevant new message",
  "Offer updated information or a meeting",
  "Stop instantly on objection or opt-out",
];

const GROWTH = [
  "Detect renewal, upgrade & cross-sell moments",
  "Recommend related services at the right time",
  "Initiate satisfaction check-ins",
  "Request reviews only from happy customers",
  "Route unhappy customers to recovery, not review requests",
];

function RevivalMock() {
  return (
    <BrowserFrame url="app.websitetech.pro/revival/opportunities">
      <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2">Re-engageable · your approval required</div>
      {[{ n: "Closed-lost · March", c: "12 leads", why: "Budget cited — new plan now fits", tone: "amber" },
        { n: "Renewal in 30 days", c: "8 customers", why: "Upsell to annual", tone: "emerald" },
        { n: "Went quiet · 60 days", c: "5 leads", why: "No reply after proposal", tone: "slate" }].map((r) => (
        <div key={r.n} className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2.5 mb-2">
          <RefreshCw size={16} className="text-brand shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-800">{r.n} · <span className="text-slate-400 font-normal">{r.c}</span></div>
            <div className="text-[11px] text-slate-400 truncate">{r.why}</div>
          </div>
        </div>
      ))}
      <div className="text-center text-xs font-semibold text-white bg-brand rounded-lg py-2 mt-1">Review & approve outreach</div>
    </BrowserFrame>
  );
}

export default function Revival() {
  useEffect(() => { window.scrollTo(0, 0); document.title = "Lead Revival & Customer Growth | AI Revenue Center"; }, []);
  return (
    <RevShell testid="rev-revival-page">
      <RevHero eyebrow="AI Revenue Center™ · Revival & Growth"
        title="Your best pipeline is" accent="the one you already have."
        sub="Two packaged automations: quietly revive cold and closed-lost leads when the timing is right, and grow existing customers with renewals, upgrades and well-timed review requests — respectfully."
        visual={<RevivalMock />} />
      <ModuleBar />

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 gap-5">
          <div className="rounded-2xl border border-slate-200 bg-white card-shadow p-7" data-testid="rev-revival-card">
            <div className="h-11 w-11 rounded-2xl bg-amber-50 text-amber-600 grid place-items-center"><RefreshCw size={20} /></div>
            <h3 className="font-heading text-xl font-extrabold text-slate-900 mt-4">Opportunity Revival</h3>
            <p className="text-sm text-slate-500 mt-1.5">Bring dormant leads back to life — only when there's a real reason to.</p>
            <ul className="mt-4 space-y-2">
              {REVIVAL.map((x) => <li key={x} className="flex items-start gap-2 text-sm text-slate-600"><ArrowRight size={14} className="text-amber-500 mt-0.5 shrink-0" /> {x}</li>)}
            </ul>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white card-shadow p-7" data-testid="rev-growth-card">
            <div className="h-11 w-11 rounded-2xl bg-emerald-50 text-emerald-600 grid place-items-center"><TrendingUp size={20} /></div>
            <h3 className="font-heading text-xl font-extrabold text-slate-900 mt-4">Customer Growth</h3>
            <p className="text-sm text-slate-500 mt-1.5">Expand happy accounts and protect at-risk ones.</p>
            <ul className="mt-4 space-y-2">
              {GROWTH.map((x) => <li key={x} className="flex items-start gap-2 text-sm text-slate-600"><ArrowRight size={14} className="text-emerald-500 mt-0.5 shrink-0" /> {x}</li>)}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white border-y border-slate-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead eyebrow="Reputation, done right" title="Ask for reviews at the right moment — from the right people" />
          <div className="grid sm:grid-cols-3 gap-4 mt-8">
            {[{ icon: Star, t: "Happy → review request", d: "Prompt satisfied customers to share their experience." },
              { icon: HeartHandshake, t: "Unhappy → recovery", d: "Route dissatisfaction to a human recovery workflow." },
              { icon: Ban, t: "Never pressure", d: "One ask, easy opt-out, no repeated nagging." }].map((x, i) => (
              <motion.div key={x.t} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
                <div className="h-10 w-10 rounded-xl bg-white text-brand grid place-items-center card-shadow"><x.icon size={18} /></div>
                <div className="font-heading font-bold text-slate-900 mt-3">{x.t}</div>
                <p className="text-sm text-slate-500 mt-1.5">{x.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <ComplianceNote>
          Revival and growth outreach obey the same rules as everything else: prior relationship or consent, suppression lists honored, and an immediate stop on any objection or opt-out.
        </ComplianceNote>
      </section>

      <RevCTA title="Wake up the pipeline you forgot you had." sub="Start with a free audit — we'll estimate how many past leads are worth re-engaging." />
    </RevShell>
  );
}
